import { SortDirection } from "client/Api/common";
import { never, UnreachableCaseError } from "client_legacy/ndaw/errors";

export enum DataSourceSortDirection {
	None = 0,
	Ascending = 1,
	Descending = 2
}

export type SortableOptions = {
	direction: DataSourceSortDirection;
	sort(newDirection: DataSourceSortDirection): void;
	disallowUnsorted?: boolean;
} | false;

export type DataSourceSortableState<TDataItem> = {
	direction: DataSourceSortDirection.None;
} | {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<TDataItem>;
};

export type SortingFor<TData> = <TProperty extends PropertyPath<TData>>(propertyName: TProperty) => SortableOptions;

export interface ISortHandler<TData> {
	sortingFor: SortingFor<TData>;
	setSorting(state: DataSourceSortableState<TData>): void;
}

export function nativeSortFunction<TValue>(
	a: TValue,
	b: TValue,
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>
): number {
	const result: number = nativeAscendingSortFunction(a, b);

	switch (direction) {
		case DataSourceSortDirection.Ascending:
			return result;
		case DataSourceSortDirection.Descending:
			return result * -1;
		default:
			throw new UnreachableCaseError(direction);
	}
}

function nativeAscendingSortFunction<TValue>(
	a: TValue,
	b: TValue
): number {
	const aIsNullOrUndefined = a === null || typeof a === "undefined";
	const bIsNullOrUndefined = b === null || typeof b === "undefined";
	if (aIsNullOrUndefined && bIsNullOrUndefined) {
		return 0;
	}

	if (aIsNullOrUndefined) {
		return -1;
	}

	if (bIsNullOrUndefined) {
		return 1;
	}

	if (typeof a === "string" && typeof b === "string") {
		if (a.localeCompare) {
			return a.localeCompare(b);
		}
	}

	return (
		a < b ? -1 :
		a > b ? 1 :
		0
	);
}

export function convertSortDirectionToDto(
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>
): SortDirection {
	switch (direction) {
		case DataSourceSortDirection.Ascending:
			return SortDirection.Ascending;
		case DataSourceSortDirection.Descending:
			return SortDirection.Descending;
		default:
			never(direction);
	}
}